<script setup>
import { computed, nextTick, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore, useStoreGetters } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';

import Dialog from 'next/dialog/Dialog.vue';
import AttributeForm from './AttributeForm.vue';

const store = useStore();
const getters = useStoreGetters();
const { t } = useI18n();

const uiFlags = getters['wgptSamlProviders/getUIFlags'];

const dialogRef = ref(null);
const isOpen = ref(false);
const formRef = ref(null);
const currentAttributeKey = ref(null);
const currentAttributeValue = ref(null);
const provider = ref({});

const existingAttributeKeys = computed(() => {
  return Object.keys(provider.value.config?.attributes || {});
});

const onClose = () => {
  isOpen.value = false;
};

const open = (selectedAttributeKey, selectedProvider) => {
  currentAttributeKey.value = selectedAttributeKey;
  currentAttributeValue.value =
    selectedProvider.config.attributes[selectedAttributeKey];
  provider.value = selectedProvider;
  isOpen.value = true;
  nextTick(() => {
    if (!dialogRef.value) return;
    dialogRef.value.open();
  });
};

const close = () => {
  dialogRef.value.close();
  onClose();
};

const handleSubmit = async () => {
  try {
    if (!formRef.value) return;
    const { validate, state } = formRef.value;

    const isFormValid = await validate();
    if (!isFormValid) return;

    const { id, config } = provider.value;

    const samlProviderUpdates = {
      id,
      config: {
        ...config,
        attributes: {
          ...config.attributes,
          [state.key]: state.value,
        },
      },
    };

    await store.dispatch('wgptSamlProviders/update', samlProviderUpdates);
    close();
  } catch (error) {
    const errorMessage =
      error?.message ||
      t('WGPT_SAML.PROVIDERS.EDIT.ATTRIBUTES.EDIT.API.ERROR_MESSAGE');
    useAlert(errorMessage);
  }
};

defineExpose({ open, close });
</script>

<template>
  <Dialog
    ref="dialogRef"
    :title="t('WGPT_SAML.PROVIDERS.EDIT.ATTRIBUTES.EDIT.TITLE')"
    :description="t('WGPT_SAML.PROVIDERS.EDIT.ATTRIBUTES.EDIT.DESCRIPTION')"
    :confirm-button-label="
      t('WGPT_SAML.PROVIDERS.EDIT.ATTRIBUTES.EDIT.CONFIRM')
    "
    :cancel-button-label="t('WGPT_SAML.PROVIDERS.EDIT.ATTRIBUTES.EDIT.CANCEL')"
    :is-loading="uiFlags.isUpdating"
    :disable-confirm-button="uiFlags.isUpdating"
    @confirm="handleSubmit"
    @close="onClose"
  >
    <AttributeForm
      v-if="isOpen"
      ref="formRef"
      :initial-key="currentAttributeKey"
      :initial-value="currentAttributeValue"
      :existing-attribute-keys="existingAttributeKeys"
      @submit="handleSubmit"
    />
  </Dialog>
</template>
